import React from 'react';
import { useTheme } from 'styled-components';
import { BadgeContent, BadgeSC } from './badge.styled';

interface BadgeProps {
  /**
   * Number to display in badge
   */
  content?: number | string;
  /**
   * Show all values, not only 99+
   */
  maxCount?: number;
  /**
   * Position badge on chosen side of component
   */
  positionHorizontal?: 'left' | 'right';
  /**
   * Position badge on chosen side of component
   */
  positionVertical?: 'top' | 'bottom';
  children?: React.ReactNode;
  style?: React.CSSProperties;
  badgeStyle?: React.CSSProperties;
}

const Badge: React.FunctionComponent<BadgeProps> = ({
  content,
  positionHorizontal,
  positionVertical,
  children,
  maxCount = 99,
  badgeStyle,
  ...rest
}) => {
  const theme = useTheme();
  const inner = React.useMemo(() => {
    if (typeof content === 'number' && content > 0) {
      return content > maxCount ? `${maxCount}+` : content;
    } else if (typeof content === 'string') {
      return content;
    }
    return null;
  }, [content]);

  const commonProps = {
    positionHorizontal,
    positionVertical,
    style: badgeStyle,
  };

  const text = (
    <BadgeContent count={typeof content === 'number' ? content : 0}>
      <span
        style={{
          color: theme.color.tint.onSolid,
          textAlign: 'center',
          alignSelf: 'center',
        }}
      >
        {inner}
      </span>
    </BadgeContent>
  );

  if (children) {
    return (
      <div {...rest}>
        {inner ? <BadgeSC {...commonProps}>{text}</BadgeSC> : null}
        {children}
      </div>
    );
  } else if (content) {
    return (
      <BadgeSC {...commonProps} standalone>
        <div>{text}</div>
      </BadgeSC>
    );
  }

  return null;
};

export default Badge;
